/**
 * OrbitLoader — global loading indicator.
 *
 * NOVA-LOAD-1 — rebuilt on the 2026 Nova character kit.
 *
 * WHY: the loader was the last surface in the app still drawing the old
 * hand-rolled, head-only Nova (`NovaThinking`, 120×160 viewBox). Every
 * other Nova moment — marketing, dashboard, toasts, onboarding — moved
 * to `@/components/nova/kit` months ago, so the single most-seen Nova in
 * the product was also the only off-model one. This renders the kit
 * avatar instead.
 *
 * WHAT WE DELIBERATELY KEPT: the rainbow. The six ROYGBV eye blocks
 * building bottom-up over 3s and the antenna ball stepping through the
 * same six colours are the part of the old loader Rennan actually
 * loves, so the hex values came across untouched (`NOVA_RAINBOW` in the
 * kit palette + `.nova-rainbow-ball` in nova.css). The kit's
 * `thinkBlocks` eye already renders the identical 6-block triangle in
 * the identical build order — it just painted them all one flat colour
 * until `eyeBlockColors` landed.
 *
 * Character Bible rule, honoured: Nova FLOATS on a dark-to-light blue
 * radial glow. Never grounded, never on legs. Same treatment as
 * `NovaSection.tsx` on marketing, factored into `.nova-loader-glow`.
 *
 * The public API ({ size, className, label }) is unchanged — ~20 call
 * sites depend on it, including App.tsx's suspense fallback.
 */

import { cn } from '@/lib/utils';
import { NovaAvatar, NOVA_RAINBOW } from '@/components/nova/kit';
// Imported HERE, not just from NovaExpression: the boot loader renders
// before any lazy Nova page, so if nova.css only rode in on a
// route-split chunk the very first Nova a user ever sees would be a
// frozen, un-animated one.
import '@/components/nova/kit/nova.css';

interface OrbitLoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  /** Optional helper text shown below Nova (e.g. "Loading clients..."). */
  label?: string;
}

/**
 * Rendered height in px. Matched to the old head-only loader's
 * footprints so no existing layout shifts.
 */
const sizeMap: Record<NonNullable<OrbitLoaderProps['size']>, number> = {
  sm: 48,
  md: 88,
  lg: 140,
  xl: 200,
};

export interface OrbitLoaderFigureProps {
  /** Rendered height (and width — the kit viewBox is square). */
  px: number;
  className?: string;
  ariaLabel?: string;
}

/**
 * Just the character + her glow, no label and no flex column.
 *
 * Exported so the splash screen can render the exact same Nova at its
 * own oversized scale instead of keeping a second copy of the recipe —
 * the two drifting apart is how the old loader ended up off-model.
 *
 * `expr-thinking` is the wrapper class nova.css keys the block-build,
 * antenna spin and body float off (`.expr-thinking .thinking-block-l-0`
 * &co). It has to be on an ancestor of the SVG or the blocks stay at
 * `opacity: 0` forever.
 */
export const OrbitLoaderFigure = ({ px, className, ariaLabel }: OrbitLoaderFigureProps) => (
  <div
    className={cn('expr-thinking relative inline-flex items-center justify-center leading-none', className)}
    style={{ width: px, height: px }}
    role="status"
    aria-label={ariaLabel ?? 'Loading'}
  >
    {/* Blue radial bed. Sits under her; breathes on its own 5.5s loop. */}
    <span aria-hidden className="nova-loader-glow" />
    <NovaAvatar
      width={px}
      eyeShape="thinkBlocks"
      // Flat fallback for the eye-glow pass; the per-block rainbow below
      // is what actually reads.
      eyeColor={NOVA_RAINBOW[0]}
      eyeBlockColors={NOVA_RAINBOW}
      antennaSpin
      antennaBallColors={NOVA_RAINBOW}
      className="relative block h-auto w-full"
    />
  </div>
);

export const OrbitLoader = ({ size = 'md', className, label }: OrbitLoaderProps) => {
  const px = sizeMap[size];
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
      <OrbitLoaderFigure px={px} ariaLabel={label ?? 'Loading'} />
      {label && (
        <p className="text-sm text-lunar-teal font-medium tracking-wide">
          {label}
        </p>
      )}
    </div>
  );
};

export default OrbitLoader;
